import type { AssetResponse } from '../../api';
import { AssetCard } from './AssetCard';
import type { AssetUrlLookup } from './useAssetUrls';
import type { OlaTokenLookup } from './useOlaTokens';

/**
 * The asset grid.
 *
 * Auto-fitting tracks rather than a fixed column count: the grid shares the
 * viewport with a details drawer that opens and closes, so the number of
 * columns has to fall out of the available width rather than be declared.
 *
 * Not virtualised, on purpose. The backend caps a page at 500 and defaults to
 * 100, and each card is a handful of nodes. The grouped view renders a
 * multi-nameplate asset once per section, so cards can outnumber assets, but
 * the costly part stays bounded by the page size: signed URLs are minted per
 * asset id, so every copy of a card shares one URL (and one cached image).
 * Virtualising starts to pay for itself if pagination gives way to infinite
 * scroll, or if assets routinely carry many nameplates rather than a few.
 */

interface AssetGridProps {
  assets: AssetResponse[];
  /**
   * Signed URLs for this page, owned by the caller.
   *
   * Fetched a level up rather than here because the detail overlay shows the
   * same assets at full size: signing inside the grid would key a second query
   * on the single open id, so opening a card would re-sign an image the grid
   * had already signed and is still holding.
   */
  urls: AssetUrlLookup;
  /**
   * Capability tokens for the OLA banners on this page, owned by the caller
   * for the same reason `urls` is — the detail overlay plays the same ads.
   */
  olaTokens: OlaTokenLookup;
  isSelected: (id: string) => boolean;
  /** Whether a click can select the asset. Absent means every asset can. */
  isSelectable?: (id: string) => boolean;
  onSelect: (id: string, modifiers: { shift?: boolean; meta?: boolean }) => void;
  onOpen: (id: string) => void;
  /**
   * Which assets match the selected brief row on the Published tab.
   *  - null  → no row picked (or not briefing): nothing to match, no card dimmed.
   *  - Set   → ids outside the set get no basket button, and are dimmed only
   *            if they cannot be selected either.
   */
  eligibleIds?: Set<string> | null;
  /**
   * Add/remove for the selected row. Passed only when there is a row to add
   * to, which is what makes the button appear on the cards at all.
   */
  onToggleBasket?: (id: string) => void;
  /** Asset ids already in the basket for the selected row. */
  basketIds?: Set<string>;
  /**
   * Set of asset IDs whose rights have lapsed (Published tab).
   */
  expiredIds?: Set<string>;
  /**
   * Set of asset IDs whose rights expire within the next 90 days (Published tab).
   */
  expiringSoonIds?: Set<string>;
  /**
   * Prefix for the cards' DOM ids, for callers that render the same asset in
   * more than one grid on the page (the grouped view's nameplate sections).
   */
  domScope?: string;
}

export function AssetGrid({
  assets,
  urls,
  olaTokens,
  isSelected,
  isSelectable,
  onSelect,
  onOpen,
  eligibleIds,
  onToggleBasket,
  basketIds,
  expiredIds,
  expiringSoonIds,
  domScope,
}: AssetGridProps) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-3">
      {assets.map((asset) => {
        const isEligible =
          eligibleIds === null || eligibleIds === undefined || eligibleIds.has(asset.id);
        const selectable = isSelectable?.(asset.id) ?? true;
        const isExpired = expiredIds?.has(asset.id) ?? false;
        const isExpiringSoon = expiringSoonIds?.has(asset.id) ?? false;
        return (
          <AssetCard
            key={asset.id}
            asset={asset}
            url={urls.urlById.get(asset.id)}
            thumbnailUrl={urls.thumbnailById.get(asset.id)}
            isUrlLoading={urls.isLoading}
            isMissing={urls.missing.has(asset.id)}
            isSelected={isSelected(asset.id)}
            isSelectable={selectable}
            onSelect={onSelect}
            onOpen={onOpen}
            onUrlError={urls.refresh}
            olaToken={olaTokens.tokenById.get(asset.id)}
            // Dimmed only where the row rules it out *and* there is nothing else
            // to do with it: a card someone can still download or edit stays lit.
            isDimmed={!isEligible && !selectable}
            isExpired={isExpired}
            isExpiringSoon={isExpiringSoon}
            domScope={domScope}
            // Only eligible and non-expired cards can be added, whether or not the
            // card itself can be selected.
            onToggleBasket={isEligible && !isExpired ? onToggleBasket : undefined}
            inBasket={basketIds?.has(asset.id) ?? false}
          />
        );
      })}
    </div>
  );
}

/** Placeholder cards shaped like the real thing, so the layout doesn't jump. */
export function AssetGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-3">
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          className="flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white"
        >
          <div className="border-b border-slate-100 bg-slate-50 px-2.5 py-1.5">
            <div className="h-3 w-3/4 animate-pulse rounded bg-slate-200" />
          </div>
          <div className="h-44 animate-pulse bg-slate-100" />
          <div className="px-2.5 py-1.5">
            <div className="h-2.5 w-1/2 animate-pulse rounded bg-slate-200" />
          </div>
        </div>
      ))}
    </div>
  );
}
